import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "./ReReprint.css";

const API_BASE = "https://hms.backend.hiragroupcloud.com";

const ReReprint = () => {
  const [searchParams] = useSearchParams();
  const [labInfo, setLabInfo] = useState(null);
  const [stampUrl, setStampUrl] = useState("");
  const accountIds = useMemo(() => {
    const ids = searchParams.get("accountIds");
    return ids ? ids : "all";
  }, [searchParams]);
  const date = searchParams.get("date") || new Date().toISOString().split("T")[0];
  const dontPrintEbilling = searchParams.get("dontPrintEbilling") === "true";
  const dontPrintZeroBalance = searchParams.get("dontPrintZeroBalance") === "true";
  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printReady, setPrintReady] = useState(false);
  const [previousRunDate, setPreviousRunDate] = useState(null);
  const [runDate, setRunDate] = useState(null);

  // -------------------------------------------------------------------------
  // Fetch the actual lab details for the lab whose statement is running.
  // Reads from localStorage first, falls back to API only if storage is empty.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const safeParse = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const buildLabInfoFromStorage = () => {
      const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";

      const storedLabInfo = safeParse(localStorage.getItem("labInfo"));
      const storedLabDetails = safeParse(localStorage.getItem("labDetails"));
      const storedUserData = safeParse(localStorage.getItem("userData"));
      const storedAddress = safeParse(localStorage.getItem("userAddress"));

      const storedLabName = localStorage.getItem("labName") || "";
      const storedLabEmail =
        localStorage.getItem("labEmail") ||
        localStorage.getItem("userEmail") ||
        "";

      const getField = (field) => {
        return (
          storedLabInfo?.[field] ||
          storedLabDetails?.[field] ||
          storedUserData?.[field] ||
          ""
        );
      };

      const resolvedEmail =
        getField("labEmail") ||
        storedLabEmail ||
        getField("email") ||
        getField("statementEmail") ||
        "";

      const merged = {
        labId: activeLabId,
        labName: getField("labName") || storedLabName || "",
        labEmail: resolvedEmail,
        address: getField("address") || storedAddress?.address || "",
        address2: getField("address2") || storedAddress?.address2 || "",
        city: getField("city") || storedAddress?.city || "",
        state: getField("state") || getField("stateProvince") || storedAddress?.state || "",
        zipCode: getField("zipCode") || getField("zipPostal") || storedAddress?.zipPostal || "",
        country: getField("country") || storedAddress?.country || "",
        telephone: getField("telephone") || "",
        // stamp (fallback if localStorage "technicianStamp" is empty)
        technicianStamp:
          storedLabInfo?.technicianStamp ||
          storedLabInfo?.preferences?.technicianStamp ||
          storedUserData?.technicianStamp ||
          storedUserData?.preferences?.technicianStamp ||
          "",
      };

      const hasAny =
        merged.labName ||
        merged.labEmail ||
        merged.address ||
        merged.city ||
        merged.telephone;
      return hasAny ? merged : null;
    };

    const fetchLabInfo = async () => {
      const fromStorage = buildLabInfoFromStorage();
      if (fromStorage) {
        setLabInfo(fromStorage);
        return;
      }

      // Fallback to API
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/api/auth/users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success && response.data.users.length > 0) {
          const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";
          const matching =
            response.data.users.find(
              (u) =>
                String(u.labId || u.userLabId || "") === String(activeLabId)
            ) || response.data.users[0];
          setLabInfo(matching);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);

  // -------------------------------------------------------------------------
  // Technician stamp (same stamp uploaded in EditPreferences)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const resolveStamp = () => {
      const raw =
        localStorage.getItem("technicianStamp") ||
        labInfo?.technicianStamp ||
        labInfo?.preferences?.technicianStamp ||
        "";

      if (!raw) {
        setStampUrl("");
        return;
      }

      if (/^https?:\/\//i.test(raw)) {
        setStampUrl(raw);
        return;
      }

      let cleanPath = raw;
      if (!cleanPath.startsWith("/")) {
        cleanPath = `/uploads/stamps/${cleanPath}`;
      } else if (!cleanPath.startsWith("/uploads")) {
        cleanPath = `/uploads/stamps/${cleanPath.replace(/^\//, "")}`;
      }
      setStampUrl(`${API_BASE}${cleanPath}`);
    };

    resolveStamp();
    window.addEventListener("stampUpdated", resolveStamp);
    return () => window.removeEventListener("stampUpdated", resolveStamp);
  }, [labInfo]);

  useEffect(() => {
    const fetchStoredStatements = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/api/statements/stored-statement`,
          {
            params: {
              accountIds,
              date,
              dontPrintEbilling: dontPrintEbilling.toString(),
              dontPrintZeroBalance: dontPrintZeroBalance.toString()
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success && response.data.data && response.data.data.statements) {
          setStatements(response.data.data.statements);
          setPreviousRunDate(response.data.data.previousRunDate || null);
          setRunDate(response.data.data.runDate || date);
          setPrintReady(true);
        } else {
          setError(response.data.message || "No stored statements found");
          setStatements([]);
        }
      } catch (error) {
        console.error("Fetch stored statements error:", error);
        setError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "An error occurred while fetching stored statements."
        );
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoredStatements();
  }, [accountIds, date, dontPrintEbilling, dontPrintZeroBalance]);

  const handlePrint = () => window.print();

  // -------------------- Formatting helpers (mirror ReprintButton) ----------
  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = String(str).replace(/^0+/, '');
    return result === '' ? '0' : result;
  };

  const formatAmountWithComma = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatCredit = (amount) => {
    if (!amount || Number(amount) === 0) return "";
    return `-£${formatAmountWithComma(Math.abs(amount))}`;
  };

  const formatDebit = (amount) => {
    if (!amount && amount !== 0) return "£0.00";
    const num = Number(amount);
    if (num === 0) return "£0.00";
    return num < 0 ? `-£${formatAmountWithComma(Math.abs(num))}` : `£${formatAmountWithComma(num)}`;
  };

  const formatBalance = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return "£0.00";
    return num < 0 ? `-£${formatAmountWithComma(Math.abs(num))}` : `£${formatAmountWithComma(num)}`;
  };

  const formatAmountWithSign = (amount) => {
    const value = Number(amount || 0);
    if (value === 0) return "£0.00";
    const formatted = Math.abs(value).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return value < 0 ? `-£${formatted}` : `£${formatted}`;
  };

  // Match ReprintButton's formatDisplayDate exactly (UTC, "DD Mon YYYY")
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Invalid Date";
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return "Invalid Date";
    const day = dateObj.getUTCDate().toString().padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${day} ${monthNames[dateObj.getUTCMonth()]} ${dateObj.getUTCFullYear()}`;
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };

  const getTruncatedDescription = (transaction) => {
    if (!transaction) return '';
    const description = transaction.displayDescription || transaction.description || '';
    if (description.includes('for Invoices:')) {
      const [label, listPart] = description.split('for Invoices:');
      if (listPart) {
        const invoiceNumbers = listPart.trim().split(',').map(num => num.trim());
        if (invoiceNumbers.length > 5) {
          const displayedInvoices = invoiceNumbers.slice(0, 5).join(', ');
          const remaining = invoiceNumbers.length - 5;
          return `${label.trim()} for Invoices: ${displayedInvoices} ... (and ${remaining} more)`;
        }
      }
    }
    return description;
  };

  // ------------------------------------------------------------------------
  // Build lines per provider — EXACTLY mirroring ReprintButton's logic:
  //   • previousBalance row (if any isPreviousBalance invoices)
  //   • current invoices
  //   • transactions
  //   • sort by date
  // ------------------------------------------------------------------------
  const buildProviderLines = (provider, statement) => {
    const lines = [];

    const prevInvoices = (provider.invoices || []).filter((inv) => inv.isPreviousBalance);
    const currentInvoices = (provider.invoices || []).filter((inv) => !inv.isPreviousBalance);

    if (prevInvoices.length > 0) {
      const prevTotal = prevInvoices.reduce((sum, inv) => sum + Number(inv.debit || 0), 0);
      const prevDateStr = statement.lastRunDate || '';
      lines.push({
        dateStr: formatDisplayDate(prevDateStr),
        desc: 'Previous Balance',
        debit: formatDebit(prevTotal),
        credit: '',
        sortDate: new Date(prevDateStr || '1970-01-01')
      });
    }

    currentInvoices.forEach((invoice) => {
      const patientName = getPatientName(invoice);
      const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
      const formattedInvoiceNumber =
        invoice.formattedInvoiceNumber || removeLeadingZeros(invoice.invoiceNumber);
      lines.push({
        dateStr: formatDisplayDate(invoiceDate),
        desc: `Case Invoice ${formattedInvoiceNumber} / ${patientName}`,
        debit: formatDebit(invoice.debit || 0),
        credit: '',
        sortDate: new Date(invoiceDate)
      });
    });

    (provider.transactions || []).forEach((trans) => {
      lines.push({
        dateStr: formatDisplayDate(trans.date),
        desc: trans.displayDescription || trans.description || '',
        debit: '',
        credit: formatCredit(trans.amount),
        sortDate: new Date(trans.date)
      });
    });

    lines.sort((a, b) => a.sortDate - b.sortDate);
    return lines;
  };

  // -------------------- Loading / Error states ---------------------------
  if (isLoading) {
    return (
      <div className="rereprint-root">
        <div className="loading-container">
          <div className="loading-spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="loading-text">Loading stored statements for {formatDisplayDate(date)}...</p>
          <p className="loading-subtext">Account: {accountIds === "all" ? "All Accounts" : accountIds}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rereprint-root">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Statements</h4>
          <p>{error}</p>
          <hr />
          <div className="mb-3">
            <p><strong>Search Criteria:</strong></p>
            <ul className="mb-0">
              <li>Date: {date} ({formatDisplayDate(date)})</li>
              <li>Account: {accountIds === "all" ? "All Accounts" : accountIds}</li>
              <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
              <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
            </ul>
          </div>
          <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>
            Try Again
          </button>
          <button className="btn btn-outline-secondary ms-2" onClick={() => window.close()}>
            Close Window
          </button>
        </div>
      </div>
    );
  }

  if (statements.length === 0) {
    return (
      <div className="rereprint-root">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">No Statements Found</h4>
          <p>No stored statements found for the selected criteria.</p>
          <div className="mt-3">
            <p><strong>Search Criteria:</strong></p>
            <ul className="mb-0">
              <li>Date: {date} ({formatDisplayDate(date)})</li>
              <li>Account: {accountIds === "all" ? "All Accounts" : accountIds}</li>
              <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
              <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
            </ul>
          </div>
          <button className="btn btn-outline-warning mt-3" onClick={() => window.close()}>
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rereprint-root">
      <div className="statement-container">
        <div className="header-controls">
          <div className="button-container">
            <button className="print-button" onClick={handlePrint} disabled={!printReady}>
              Print
            </button>
          </div>
        </div>

        {statements
          .slice()
          .sort((a, b) => {
            const nameA = (a.accountHolder || "").toLowerCase();
            const nameB = (b.accountHolder || "").toLowerCase();
            return nameA.localeCompare(nameB);
          })
          .map((statement, index) => {
            // EXACT same providerList construction as ReprintButton:
            // filter out empty groups, sort general first then by name.
            const providerList = Object.values(statement.groupedByProvider || {})
              .filter(
                (group) =>
                  (group.invoices && group.invoices.length > 0) ||
                  (group.transactions && group.transactions.length > 0)
              )
              .sort((a, b) => {
                if (a.providerId === 'general') return -1;
                if (b.providerId === 'general') return 1;
                return (a.providerName || '').localeCompare(b.providerName || '');
              });

            const previousRunDateForAccount =
              previousRunDate || statement.previousRunDate || statement.lastRunDate;

            return (
              <div
                key={`${statement.accountId}-${index}`}
                className="statement-section"
                style={{ pageBreakBefore: index > 0 ? "always" : "auto" }}
              >
                {/* Header */}
                <div className="header-section">
                  <div className="lab-info">
                    <strong>{labInfo?.labName || "Hira Dental Laboratory"}</strong>
                    <br />
                    {labInfo?.address || "02 Ulverscroft Road"}
                    <br />
                    {[
                      labInfo?.city,
                      labInfo?.state,
                      labInfo?.zipCode || labInfo?.zipPostal,
                    ]
                      .filter(Boolean)
                      .join(", ") || "Coventry, CV3 5EZ"}
                    <br />
                    {labInfo?.telephone
                      ? `Tel: ${labInfo.telephone}`
                      : "Tel: 024 7642 2500"}
                  </div>
                  <div className="header-right">
                    <div className="statement-right">
                      <strong>Statement for {formatDisplayDate(statement.displayDate || runDate)}</strong>
                      <br />
                      {labInfo?.labEmail || "info@hiradentallab.com"}
                    </div>
                    <div className="logo-container-right">
                      {stampUrl && (
                        <img
                          className="header-stamp"
                          src={stampUrl}
                          alt="Technician Stamp"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <h3 className="statement-title">
                  Statement for {formatDisplayDate(statement.displayDate || runDate)}
                </h3>

                {/* Client + payment details */}
                <div className="client-payment-wrapper">
                  <div className="client-info">
                    <strong>{statement.accountHolder}</strong>
                    <br />
                    {statement.address || "Address not available"}
                    <br />
                    {[statement.city, statement.state, statement.zip].filter(Boolean).join(", ")}
                    <br />
                    {statement.telephone && statement.telephone !== "Not available"
                      ? `Tel: ${statement.telephone}`
                      : "Tel: 024 7642 2500"}
                    <br />
                    {statement.email && statement.email !== "Email not available"
                      ? `Email: ${statement.email}`
                      : "Email: accounts@hiradentallab.com"}
                  </div>
                  <div className="payment-details">
                    {statement.note ? (
                      statement.note.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        <div>Please remit payment to:</div>
                        <div>Hira Dental Laboratory</div>
                        <div>Sort Code: 20-00-00</div>
                        <div>Account No: 12345678</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Main invoice/transaction table */}
                <div className="table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providerList.map((provider) => {
                        const lines = buildProviderLines(provider, statement);
                        const displayProviderName =
                          provider.providerId === 'general' ? 'General' : provider.providerName;

                        return (
                          <React.Fragment key={provider.providerId}>
                            <tr className="provider-row">
                              <td colSpan="4"><strong>{displayProviderName}</strong></td>
                            </tr>

                            {lines.length > 0 ? (
                              lines.map((line, lineIdx) => (
                                <tr key={`${provider.providerId}-line-${lineIdx}`}>
                                  <td>{line.dateStr}</td>
                                  <td>{line.desc}</td>
                                  <td>{line.debit}</td>
                                  <td>{line.credit}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="no-transactions">
                                  No transactions for this provider
                                </td>
                              </tr>
                            )}

                            {/* Subtotal — EXACT match with ReprintButton */}
                            {providerList.length > 1 && (
                              <tr className="subtotal-row">
                                <td colSpan="2"><strong>Subtotal</strong></td>
                                {provider.providerId === 'general' ? (
                                  <>
                                    <td></td>
                                    <td>{formatCredit(provider.newBalance || 0)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td>{formatDebit(provider.newBalance || 0)}</td>
                                    <td></td>
                                  </>
                                )}
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Aging summary — read account-level stored values */}
                <div className="table-wrapper">
                  <table className="statement-table center">
                    <thead>
                      <tr>
                        <th>1-30 Days</th>
                        <th>31-60 Days</th>
                        <th>61-90 Days</th>
                        <th>91+ Days</th>
                        <th>New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{formatBalance(statement.oneTo30Days)}</td>
                        <td>{formatBalance(statement.thirtyOneTo60Days)}</td>
                        <td>{formatBalance(statement.sixtyOneTo90Days)}</td>
                        <td>{formatBalance(statement.ninetyPlusDays)}</td>
                        <td>{formatBalance(statement.newBalance)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="statement-footer">
                  <div>{statement.accountHolder}</div>
                  <div>{statement.footer  || "Thank you for your business"}</div>
                  <div>Page {index + 1} of {statements.length}</div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ReReprint;